import React, { useEffect, useState } from "react";
import { useAuditLog } from "@/hooks/useLocalStorage";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Filter,
  Clock,
  X,
  Search,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaginationControl } from "@/widgets/renderPagination";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Popup } from "@/widgets/RuleDetailDailog";
import { useBreadcrumbs } from "./BreadcrumbContext";

const DEFAULT_FILTERS = {
  errorCode: "ALL",
  statusCode: "",
  dateFrom: "",
  dateTo: "",
};

const errorTypeColor: Record<string, string> = {
  AUTH_ERROR: "bg-red-100 text-red-800",
  DATABASE_ERROR: "bg-orange-100 text-orange-800",
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleString();


export function AuditLog() {
  // UI filter inputs
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Applied filters (used by API)
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [showFilters, setShowFilters] = useState(false);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(10);

  const [showPopup, setShowPopup] = useState(false);
  const [dataHead, setDataHead] = useState<any | null>(null);
  const [dataContent, setDataContent] = useState<any | null | undefined>("");
  const [popupName, setPopupName] = useState("");

  const processedFilters = {
    errorCode:
      appliedFilters.errorCode === "ALL"
        ? ""
        : appliedFilters.errorCode,
    statusCode: appliedFilters.statusCode || undefined,
    dateFrom: appliedFilters.dateFrom || undefined,
    dateTo: appliedFilters.dateTo || undefined,
    pageSize: pageSize,
    pageIndex: pageIndex,
  };

  const { data: auditEntries, isLoading } =
    useAuditLog(processedFilters);

  console.log("auditEntries", auditEntries)

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setTotalCount(auditEntries?.total ?? 0)

  }, [auditEntries])

  const handleSearch = () => {
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const { setBreadcrumbs } = useBreadcrumbs();
  
      React.useEffect(() => {
          setBreadcrumbs([
              {
                  label: `Audit Log`,
                  path: `/audit`,
              },
          ]);
      }, []);

  return (
    <div className="min-h-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-black">
            Audit Log
          </h2>
          <p className="text-black mt-1">
            System errors and failures captured by the API
          </p>
        </div>

        <Button
          size="sm"
          onClick={() => setShowFilters((v) => !v)}
          className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
        >
          Filters
        </Button>
      </div>

      {showFilters && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Error Type</Label>
                <Select
                  value={filters.errorCode}
                  onValueChange={(v) =>
                    handleFilterChange("errorCode", v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All error types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AI_ERROR">AI_ERROR</SelectItem>
                    <SelectItem value="DATABASE_ERROR">DATABASE_ERROR</SelectItem>
                    <SelectItem value="AUTH_ERROR">AUTH_ERROR</SelectItem>
                    <SelectItem value="VALIDATION_ERROR">VALIDATION_ERROR</SelectItem>
                    <SelectItem value="TIMEOUT_ERROR">TIMEOUT_ERROR</SelectItem>
                    <SelectItem value="NOT_FOUND">NOT_FOUND</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status Code</Label>
                <Select
                  value={filters.statusCode}
                  onValueChange={(v) =>
                    handleFilterChange("statusCode", v)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="400">400</SelectItem>
                    <SelectItem value="401">401</SelectItem>
                    <SelectItem value="403">403</SelectItem>
                    <SelectItem value="422">422</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>

                </Select>
              </div>

              <div>
                <Label>Date From</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                />
              </div>

              <div>
                <Label>Date To</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) =>
                    handleFilterChange(
                      "dateTo",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>

            <div className="flex justify-center gap-3 mt-6">
              <Button
                onClick={handleSearch}
                className="flex items-center gap-2"
              >
                Search
              </Button>

              <Button
                variant="outline"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                Reset
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowFilters(false)}
                className="flex items-center gap-2"
              >
                Close
              </Button>


            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Error Logs {auditEntries && `(${auditEntries.total})`}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="py-6 text-sm text-black">
              Loading audit logs…
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Error Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Request</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {auditEntries?.logs?.map((entry: any, index: any) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-black" />
                            {formatDate(entry.timestamp)}
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge
                            className={
                              errorTypeColor[entry.error_type]
                            }
                          >
                            {entry.error_type}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <Badge variant="outline">
                            {entry.status_code}
                          </Badge>
                        </TableCell>

                        <TableCell className="max-w-xs truncate">
                          <Button
                            variant={"link"}
                            onClick={() => {
                              setPopupName("Message")
                              setDataContent(entry.error_message);
                              setShowPopup(true);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>

                        <TableCell className="text-xs">
                          <div className="font-medium">
                            {entry.request.method}
                          </div>
                          <div className="truncate text-black">
                            {entry.request.path}
                          </div>
                        </TableCell>

                        <TableCell className="font-mono text-xs">
                          {entry.ip_address}
                        </TableCell>

                        <TableCell className="max-w-xs truncate">
                          <Button
                            variant={"link"}
                            onClick={() => {
                              setPopupName("Detail")
                              setDataHead({ "Name": "" });
                              setDataContent(entry.error_details);
                              setShowPopup(true);
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationControl
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPageIndex}
                />
              </div>

              {
                showPopup && (
                  <Popup
                    popupName={popupName}
                    heading={dataHead}
                    content={dataContent}
                    onClose={() => setShowPopup(false)}
                  />
                )
              }

              {auditEntries?.length === 0 && (
                <div className="text-center py-8 text-black">
                  <Calendar className="h-8 w-8 mx-auto mb-2" />
                  <p>No error logs found.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
